import { getAccessToken, logout } from '@/api/bbsworld/auth';
import { User } from '@/types';
import EventEmitter from 'events';
import jwtDecode from 'jwt-decode';

const MILLISECOND_PER_SECOND = 1000;
const SECOND_MS = 1000;
const MINUTE_MS = 60 * SECOND_MS;

type AccessTokenPayload = {
  iss: string;
  iat: number;
  exp: number;
  user_id: number;
  is_active: boolean;
  username: string;
  email: string;
  is_staff: boolean;
  is_superuser: boolean;
};

export class ClientAuth {
  public static emitter = new EventEmitter();
  public static headers: Record<string, string> = {};
  public static isClient: boolean = typeof window !== 'undefined';
  public static user: User | null = null;
  protected static isRefreshScheduleRunning: boolean = false;
  protected static refreshSchedule?: NodeJS.Timeout = this.isClient
    ? setInterval(() => {
        if (this.accessToken) {
          try {
            const payload = jwtDecode(this.accessToken) as AccessTokenPayload;
            const expiredAt = payload.exp * MILLISECOND_PER_SECOND;
            const remainingTime = expiredAt - Date.now();
            const isSoonExpire = remainingTime < 3 * MINUTE_MS;
            const isAlreadyExpired = remainingTime < 0;

            // ! 결국 토큰이 만료되어버린 경우
            if (isAlreadyExpired) {
              throw new Error('토큰이 만료되어 재발급합니다.');
            } else if (isSoonExpire) {
              throw new Error('토큰 만료가 얼마 남지않아 재발급합니다.');
            }
          } catch {
            if (this.isClient && navigator.onLine) {
              this.refresh();
            }
          }
        }
      }, 5 * SECOND_MS)
    : undefined;

  public static _accessToken: string = '';
  public static get accessToken(): string {
    return this._accessToken;
  }
  public static set accessToken(value: string) {
    if (this._accessToken === value) return;

    if (this._accessToken) {
      try {
        const beforePayload = jwtDecode(this._accessToken) as AccessTokenPayload;
        const afterPayload = jwtDecode(value) as AccessTokenPayload;
        if (afterPayload.exp > beforePayload.exp) {
          if (value) {
            this.headers['Authorization'] = `Bearer ${value}`;
            this.emitter.emit('refresh');
          } else {
            delete this.headers['Authorization'];
          }
          this._accessToken = value;
        }
      } catch {
        console.error('토큰 새로고침 오류');
      }
    } else {
      if (value) {
        this.headers['Authorization'] = `Bearer ${value}`;
        this.emitter.emit('refresh');
      } else {
        delete this.headers['Authorization'];
      }
      this._accessToken = value;
    }
  }

  public static async refresh(): Promise<boolean> {
    if (!this.isClient) return false;
    if (this.isRefreshScheduleRunning) return false;
    if (!window.navigator.onLine) return false;

    this.isRefreshScheduleRunning = true;

    try {
      const token = await getAccessToken();

      if (!token) return false;

      this.accessToken = token;

      return true;
    } catch (e) {
      console.error('토큰 새로고침 오류', e);

      return false;
    } finally {
      this.isRefreshScheduleRunning = false;
    }
  }

  public static async logout(): Promise<void> {
    try {
      await logout();
    } catch (e) {
      console.error('로그아웃 오류', e);
    } finally {
      this._accessToken = '';
      this.user = null;
      delete this.headers['Authorization'];
    }
  }
}
